"use client";

import dynamic from "next/dynamic";
import { useV1GroupLocations } from "@/lib/hooks/useGroups";

// Leaflet touches `window` at import time, so the map must never render on the
// server.
const MapInner = dynamic(() => import("./GroupExploreMapInner"), {
  ssr: false,
  loading: () => (
    <div className="h-full w-full flex items-center justify-center text-sm text-subtle-foreground">
      Indlæser kort…
    </div>
  ),
});

/**
 * THE MAP READS A FACET NOW, NOT A LIST OF GROUPS
 *
 * `GET /theta/groups/locations` shipped EVERY located group in one unpaged
 * response — thirty thousand rows, each with a name, a cover URL and two
 * member counts — so that the client could group them by place and draw a dot
 * per group. `GET /group-locations` is the AGGREGATE the map actually wanted:
 * one row per distinct `baseLocation` with a `groupCount`, plus the row whose
 * `location` is `null` carrying the "uden placering" figure that the legacy
 * `meta.without_location` did.
 *
 * SO THE MARKERS CHANGED MEANING, deliberately: a marker is a PLACE and its
 * size is how many groups are there. Clicking it opens
 * `/grupper?location=<token>`, which is `GET /groups?location=` — the SAME
 * predicate the facet counted with (§2.14), so a marker that says four cannot
 * open a list of three.
 *
 * `?hasLocation=true` is a DIFFERENT thing and is not this: it is a boolean
 * filter on the groups collection, and reading it as the facet would be 300
 * pages of groups to draw a map.
 *
 * NO `meta.scope`. The route's own description is explicit that nothing here
 * counts a group the caller may not know exists, so there is no
 * administratively-scoped variant to disclose and none to render.
 */
export function GroupExploreMap({ parentSlug }: { parentSlug?: string } = {}) {
  const { data, isLoading, isError, refetch } = useV1GroupLocations(parentSlug);

  const rows = data ?? [];
  // The `null` row is the groups that declared no place. It is ordered onto
  // page one by the route precisely so this figure is never behind a cursor.
  const withoutLocation = rows.find((row) => row.location === null)?.groupCount ?? 0;
  const places = rows.filter((row) => row.location !== null);

  if (isError) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-sm text-subtle-foreground px-6 text-center">
        Kortet kunne ikke indlæses.
        <button
          type="button"
          onClick={() => refetch()}
          className="text-primary underline"
        >
          Prøv igen
        </button>
      </div>
    );
  }

  if (!isLoading && places.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center text-sm text-subtle-foreground px-6 text-center">
        Ingen grupper har valgt en placering endnu.
      </div>
    );
  }

  return <MapInner places={places} withoutLocation={withoutLocation} />;
}
